/**
 * Pantalla de entrada del kiosco.
 *
 * Dos modos, y el que manda lo dice el BACKEND (`GET /api/auth/modo`), nunca
 * el navegador:
 *
 *  - **Kiosco** — el equipo esta registrado como de confianza. Se pide un PIN
 *    de 6 digitos con un teclado numerico enorme. El usuario principal tiene
 *    88 anos y entra cada manana desde el mismo sitio: teclear una contrasena
 *    larga no es una molestia, es una barrera. Sesion de 12 h.
 *  - **Remoto** — cualquier otro sitio. Usuario + contrasena completa y sesion
 *    de 2 h.
 *
 * Accesibilidad (mismo criterio que el resto del kiosco): tipografia de 24 px
 * hacia arriba, objetivos tactiles de 88 px, contraste alto, foco visible y
 * cada boton con su etiqueta en texto. Nada de iconos sueltos.
 *
 * Esta pantalla NO decide nada de seguridad: solo recoge credenciales y las
 * manda. Quien decide es el backend.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Boton } from '@/components/ui/button';
import { api, ErrorApi, type ModoAcceso, type SesionAbierta } from '@/lib/api';
import { cn } from '@/lib/utils';

/** Longitud del PIN del kiosco (debe casar con `[auth] longitud_pin`). */
const LONGITUD_PIN = 6;

/** Teclas del teclado numerico, en el orden de un telefono. */
const TECLAS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const;

export interface PropsLogin {
  /** Modo que ha dictado el backend. `null` mientras se consulta. */
  acceso: ModoAcceso | null;
  /** Se llama con la sesion recien abierta. */
  alEntrar: (sesion: SesionAbierta) => void;
  /** true si se llega aqui porque la sesion caduco (no por arranque). */
  caducada?: boolean;
  className?: string;
}

export function Login({ acceso, alEntrar, caducada = false, className }: PropsLogin) {
  const esKiosco = acceso?.modo === 'kiosco';
  const [conPin, setConPin] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // El modo real solo se conoce cuando responde el backend.
  useEffect(() => {
    setConPin(esKiosco);
  }, [esKiosco]);

  const entrar = useCallback(
    async (accion: () => Promise<SesionAbierta>) => {
      setEnviando(true);
      setError(null);
      try {
        alEntrar(await accion());
      } catch (e) {
        // El backend ya redacta mensajes aptos para leerse en pantalla y
        // deliberadamente ambiguos (nunca dice si el usuario existe).
        setError(
          e instanceof ErrorApi
            ? e.message
            : 'No he podido comprobar sus datos. Intentelo otra vez.',
        );
        throw e;
      } finally {
        setEnviando(false);
      }
    },
    [alEntrar],
  );

  return (
    <div
      className={cn(
        'flex h-full w-full items-center justify-center bg-background p-6',
        className,
      )}
    >
      <div className="tarjeta-kiosco flex w-full max-w-2xl flex-col gap-8 p-10">
        <header className="flex flex-col gap-3">
          <h1 className="text-4xl font-bold">Androcles</h1>
          <p className="text-2xl text-muted-foreground">
            {acceso === null
              ? 'Un momento…'
              : caducada
                ? 'Su sesión ha caducado. Vuelva a entrar, no se ha perdido nada.'
                : esKiosco
                  ? `Buenos dias. ${acceso.alias ?? 'Kiosco'}.`
                  : 'Identifiquese para continuar.'}
          </p>
        </header>

        {error && (
          <p
            role="alert"
            aria-live="assertive"
            className="rounded-lg border-2 border-destructive bg-destructive/10 p-5 text-2xl font-semibold text-destructive"
          >
            {error}
          </p>
        )}

        {acceso !== null &&
          (esKiosco && conPin ? (
            <FormularioPin enviando={enviando} alEnviar={(pin) => entrar(() => api.loginPin(pin))} />
          ) : (
            <FormularioPassword
              enviando={enviando}
              alEnviar={(usuario, password) => entrar(() => api.login(usuario, password))}
            />
          ))}

        {esKiosco && (
          <Boton
            variante="fantasma"
            tamano="ancho"
            onClick={() => {
              setError(null);
              setConPin((v) => !v);
            }}
          >
            {conPin ? 'Prefiero usar mi usuario y contraseña' : 'Volver al teclado numerico'}
          </Boton>
        )}

        {!esKiosco && acceso !== null && (
          <p className="text-lg text-muted-foreground">
            Este equipo no esta registrado como kiosco, asi que hace falta la contrasena
            completa. La sesion durara 2 horas.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Teclado numerico del PIN.
 *
 * Botones de 7 rem de alto y numeros de 40 px: se acierta sin gafas y sin
 * precision fina. El PIN se envia solo al completar los 6 digitos, para que
 * no haya que buscar ningun boton de confirmar.
 */
function FormularioPin({
  enviando,
  alEnviar,
}: {
  enviando: boolean;
  alEnviar: (pin: string) => Promise<unknown>;
}) {
  const [pin, setPin] = useState('');
  const enviandoRef = useRef(false);

  const pulsar = useCallback(
    (digito: string) => {
      if (enviandoRef.current) return;
      setPin((actual) => (actual.length >= LONGITUD_PIN ? actual : actual + digito));
    },
    [],
  );

  // Envio automatico al completar el PIN: el jefe no tiene que buscar "Entrar".
  useEffect(() => {
    if (pin.length !== LONGITUD_PIN || enviandoRef.current) return;
    enviandoRef.current = true;
    void alEnviar(pin)
      .catch(() => undefined)
      .finally(() => {
        enviandoRef.current = false;
        setPin('');
      });
  }, [pin, alEnviar]);

  // El kiosco tiene teclado fisico: que tambien valga para teclear el PIN.
  useEffect(() => {
    const alPulsarTecla = (evento: KeyboardEvent) => {
      if (evento.key >= '0' && evento.key <= '9') pulsar(evento.key);
      else if (evento.key === 'Backspace') setPin((p) => p.slice(0, -1));
    };
    window.addEventListener('keydown', alPulsarTecla);
    return () => window.removeEventListener('keydown', alPulsarTecla);
  }, [pulsar]);

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-3">
        <span className="text-2xl font-semibold" id="etiqueta-pin">
          Su clave de seis numeros
        </span>
        <div
          className="flex justify-center gap-4"
          role="status"
          aria-labelledby="etiqueta-pin"
          aria-label={`${pin.length} de ${LONGITUD_PIN} numeros introducidos`}
        >
          {Array.from({ length: LONGITUD_PIN }).map((_, indice) => (
            <span
              key={indice}
              className={cn(
                'h-12 w-12 rounded-full border-4 border-border',
                indice < pin.length && 'border-primary bg-primary',
              )}
              aria-hidden="true"
            />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {TECLAS.map((tecla) => (
          <Boton
            key={tecla}
            variante="contorno"
            tamano="grande"
            className="text-[2.5rem] font-bold"
            disabled={enviando}
            onClick={() => pulsar(tecla)}
            aria-label={`Numero ${tecla}`}
          >
            {tecla}
          </Boton>
        ))}

        <Boton
          variante="fantasma"
          tamano="grande"
          className="text-2xl"
          disabled={enviando || pin.length === 0}
          onClick={() => setPin((p) => p.slice(0, -1))}
        >
          Borrar
        </Boton>

        <Boton
          variante="contorno"
          tamano="grande"
          className="text-[2.5rem] font-bold"
          disabled={enviando}
          onClick={() => pulsar('0')}
          aria-label="Numero 0"
        >
          0
        </Boton>

        <Boton
          variante="fantasma"
          tamano="grande"
          className="text-2xl"
          disabled={enviando || pin.length === 0}
          onClick={() => setPin('')}
        >
          Empezar de nuevo
        </Boton>
      </div>

      {enviando && <p className="text-center text-2xl text-muted-foreground">Comprobando…</p>}
    </div>
  );
}

/** Formulario clasico de usuario y contrasena (modo remoto). */
function FormularioPassword({
  enviando,
  alEnviar,
}: {
  enviando: boolean;
  alEnviar: (usuario: string, password: string) => Promise<unknown>;
}) {
  const [usuario, setUsuario] = useState('');
  const [password, setPassword] = useState('');

  const clases =
    'min-h-tactil w-full rounded-lg border-2 border-input bg-card px-6 text-2xl ' +
    'text-card-foreground outline-none focus:border-primary focus:ring-2 focus:ring-ring';

  return (
    <form
      className="flex flex-col gap-6"
      onSubmit={(evento) => {
        evento.preventDefault();
        if (!usuario || !password || enviando) return;
        void alEnviar(usuario, password).catch(() => setPassword(''));
      }}
    >
      <label className="flex flex-col gap-3 text-2xl font-semibold">
        Usuario
        <input
          className={clases}
          type="text"
          name="usuario"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          value={usuario}
          onChange={(e) => setUsuario(e.target.value)}
          disabled={enviando}
        />
      </label>

      <label className="flex flex-col gap-3 text-2xl font-semibold">
        Contrasena
        <input
          className={clases}
          type="password"
          name="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={enviando}
        />
      </label>

      <Boton
        type="submit"
        variante="principal"
        tamano="grande"
        className="w-full"
        disabled={enviando || !usuario || !password}
      >
        {enviando ? 'Comprobando…' : 'Entrar'}
      </Boton>
    </form>
  );
}

export default Login;
